import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  ReactNode,
} from "react";
import Loader from "../lib/web-components/Loader/Loader";

interface LoadingContextType {
  loading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
}

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingContext = createContext<LoadingContextType | undefined>(
  undefined,
);

/**
 * The overlay, counted rather than flagged.
 *
 * This held a boolean, so `stopLoading` meant "hide it" no matter who else was
 * still waiting. Two concurrent loads and the first one to finish took the
 * overlay down while the second ran on — the user got a live-looking page over
 * a request still in flight, which is how they end up editing rows that are
 * about to be replaced.
 *
 * A count fixes that without any call site changing: the overlay is up while
 * anything is loading, and comes down when the last one finishes.
 *
 * **Clamped at zero, deliberately.** Not every stop has a matching start. The
 * page's clear-on-filter-change effects raise the overlay with no natural
 * counterpart; they book their raises in useOverlayDebt, and the release
 * effect (and the empty-departments one) repays exactly that debt — no more,
 * so an unrelated request still in flight keeps the overlay up rather than
 * having it pulled from under it. Clamping is what makes a stray unmatched
 * stop harmless instead of pushing the count negative, where a later start
 * would look like it did nothing.
 *
 * Updates are functional so two starts in the same tick cannot read the same
 * stale value and count as one.
 */
export const LoadingProvider: React.FC<LoadingProviderProps> = ({
  children,
}) => {
  const [pendingCount, setPendingCount] = useState(0);

  const startLoading = useCallback(
    () => setPendingCount((count) => count + 1),
    [],
  );
  const stopLoading = useCallback(
    () => setPendingCount((count) => Math.max(0, count - 1)),
    [],
  );

  const loading = pendingCount > 0;

  return (
    <LoadingContext.Provider value={{ loading, startLoading, stopLoading }}>
      {children}
      {loading && <Loader />}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};
